import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getBranding,
  upsertBranding,
  uploadLogo,
  BrandingPayload,
} from "../../services/hospitalMdtBranding.service";
import {
  getStructure,
  MdtReportStructure,
} from "../../services/mdtReportStructure.service";
import { getHospital, Hospital } from "../../services/superAdmin.service";
import MdtReportPreview from "./components/MdtReportPreview";
import {
  ArrowLeft,
  Loader2,
  Upload,
  X,
  Building2,
  FileText,
  Save,
  Eye,
  ClipboardList,
} from "lucide-react";

const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/svg+xml"];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const INITIAL_FORM: BrandingPayload = {
  logoUrl: null,
  headerText: "",
  hospitalNameSnapshot: "",
  addressSnapshot: "",
  contactSnapshot: "",
  signatureLeftLabel: "Lead Oncologist Signature",
  signatureRightLabel: "MDT Coordinator Verification",
  footerText: "",
};

type Tab = "edit" | "preview";

interface MDTTemplateEditorProps {
  hospitalId: string;
  onBack?: () => void;
}

const MDTTemplateEditor = ({ hospitalId, onBack }: MDTTemplateEditorProps) => {
  const navigate = useNavigate();
  const goBack = useCallback(() => {
    if (onBack) onBack();
    else navigate("/mdt-templates?tab=branding");
  }, [onBack, navigate]);

  const [form, setForm] = useState<BrandingPayload>(INITIAL_FORM);
  const [hospital, setHospital] = useState<Hospital | null>(null);
  const [structure, setStructure] = useState<MdtReportStructure | null>(null);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("edit");
  const [logoUploading, setLogoUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!hospitalId) return;
    (async () => {
      setFetching(true);
      try {
        const hosp = await getHospital(hospitalId);
        if (hosp.status !== "ACTIVE") {
          toast.error("Branding can only be configured for active hospitals");
          goBack();
          return;
        }
        setHospital(hosp);

        getStructure()
          .then(setStructure)
          .catch(() => setStructure(null));

        let branding: BrandingPayload | null = null;
        try {
          const b = await getBranding(hospitalId);
          branding = {
            logoUrl: b.logoUrl ?? null,
            headerText: b.headerText || "",
            hospitalNameSnapshot: b.hospitalNameSnapshot || hosp.hospitalName,
            addressSnapshot: b.addressSnapshot || composeAddress(hosp) || "",
            contactSnapshot: b.contactSnapshot || composeContact(hosp) || "",
            signatureLeftLabel: b.signatureLeftLabel || "Lead Oncologist Signature",
            signatureRightLabel: b.signatureRightLabel || "MDT Coordinator Verification",
            footerText: b.footerText || "",
          };
        } catch {
          // No branding yet -> new mode, prefilled from hospital
          branding = {
            ...INITIAL_FORM,
            hospitalNameSnapshot: hosp.hospitalName,
            addressSnapshot: composeAddress(hosp) || "",
            contactSnapshot: composeContact(hosp) || "",
          };
        }
        setForm(branding);
      } catch {
        toast.error("Hospital not found");
        goBack();
      } finally {
        setFetching(false);
      }
    })();
  }, [hospitalId, goBack]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleLogoFile = (file: File | null) => {
    if (!file) return;
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      toast.error("Only PNG, JPG, SVG allowed");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("File size must be under 2MB");
      return;
    }
    setErrors((prev) => {
      const next = { ...prev };
      delete next.logo;
      return next;
    });
    setLogoUploading(true);
    uploadLogo(hospitalId, file)
      .then((result) => {
        setForm((prev) => ({ ...prev, logoUrl: result.url }));
        toast.success("Logo uploaded");
      })
      .catch(() => toast.error("Failed to upload logo"))
      .finally(() => setLogoUploading(false));
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleLogoFile(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0] || null;
    handleLogoFile(file);
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.hospitalNameSnapshot?.trim()) errs.hospitalNameSnapshot = "Hospital name is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await upsertBranding(hospitalId, { ...form });
      toast.success("Hospital branding saved");
      goBack();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save hospital branding");
    } finally {
      setSaving(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const previewBranding = {
    logoUrl: form.logoUrl,
    headerText: form.headerText,
    hospitalName: form.hospitalNameSnapshot,
    addressSnapshot: form.addressSnapshot,
    contactSnapshot: form.contactSnapshot,
    signatureLeftLabel: form.signatureLeftLabel,
    signatureRightLabel: form.signatureRightLabel,
    footerText: form.footerText,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={goBack}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            aria-label="Back to hospital branding"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
              {form.hospitalNameSnapshot || hospital?.hospitalName || "Hospital"}
              <span className="text-gray-400 font-normal"> — branding</span>
            </h1>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Save branding
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-6 border-b border-gray-200">
        <TabButton active={activeTab === "edit"} onClick={() => setActiveTab("edit")}>
          <ClipboardList size={16} />
          Edit
        </TabButton>
        <TabButton active={activeTab === "preview"} onClick={() => setActiveTab("preview")}>
          <Eye size={16} />
          Preview
        </TabButton>
      </div>

      {activeTab === "edit" ? (
        <div className="space-y-6">
          {/* Hospital details */}
          <Card
            icon={<Building2 size={18} />}
            title="Hospital details"
            description="Name, address and contact are prefilled from the hospital and can be edited per branding."
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Logo</label>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  className={`relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-5 text-center transition-colors ${
                    errors.logo
                      ? "border-red-400 bg-red-50"
                      : dragOver
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-300 bg-gray-50 hover:border-primary-400 hover:bg-primary-50/50"
                  }`}
                >
                  {form.logoUrl ? (
                    <>
                      <div className="relative h-20 w-20 rounded-lg overflow-hidden border border-gray-200 bg-white mb-3">
                        <img src={form.logoUrl} alt="Hospital logo" className="h-full w-full object-contain" />
                        <button
                          type="button"
                          onClick={() => setForm((p) => ({ ...p, logoUrl: null }))}
                          className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-white shadow flex items-center justify-center text-gray-500 hover:text-red-600"
                          aria-label="Remove logo"
                        >
                          <X size={12} />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">Drag & drop or click to replace</p>
                    </>
                  ) : (
                    <>
                      <div className="h-10 w-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center mb-2">
                        <Upload size={18} />
                      </div>
                      <p className="text-sm font-medium text-gray-700">Upload logo</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG or SVG · max 2MB</p>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={handleFileInput}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={logoUploading}
                  />
                  {logoUploading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                      <Loader2 size={20} className="animate-spin text-primary-600" />
                    </div>
                  )}
                </div>
                {errors.logo && <p className="text-xs text-red-600 mt-1.5">{errors.logo}</p>}
              </div>

              {/* Name + address + contact (editable per branding) */}
              <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Hospital name"
                  name="hospitalNameSnapshot"
                  value={form.hospitalNameSnapshot || ""}
                  onChange={handleChange}
                  required
                  error={errors.hospitalNameSnapshot}
                />
                <Field
                  label="Contact"
                  name="contactSnapshot"
                  value={form.contactSnapshot || ""}
                  onChange={handleChange}
                />
                <div className="sm:col-span-2">
                  <Field
                    label="Address"
                    name="addressSnapshot"
                    value={form.addressSnapshot || ""}
                    onChange={handleChange}
                    textarea
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Report header */}
          <Card
            icon={<FileText size={18} />}
            title="Report header"
            description="Optional header text shown alongside the hospital name at the top of the report."
          >
            <Field
              label="Header text"
              name="headerText"
              value={form.headerText || ""}
              onChange={handleChange}
              textarea
              placeholder="e.g. Clinical Outcomes Institute"
            />
          </Card>

          {/* Signature block */}
          <Card
            icon={<FileText size={18} />}
            title="Signature block"
            description="Customize the two signature labels shown at the bottom of the report."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                label="Left signature label"
                name="signatureLeftLabel"
                value={form.signatureLeftLabel || ""}
                onChange={handleChange}
                placeholder="Lead Oncologist Signature"
              />
              <Field
                label="Right signature label"
                name="signatureRightLabel"
                value={form.signatureRightLabel || ""}
                onChange={handleChange}
                placeholder="MDT Coordinator Verification"
              />
            </div>
          </Card>

          {/* Footer disclaimer */}
          <Card
            icon={<FileText size={18} />}
            title="Footer disclaimer"
            description="Set the footer disclaimer shown at the end of the report."
          >
            <div className="grid grid-cols-1 gap-4">
              <Field
                label="Footer disclaimer"
                name="footerText"
                value={form.footerText || ""}
                onChange={handleChange}
                textarea
                placeholder="e.g. This is an automated report generated by the system."
              />
            </div>
          </Card>
        </div>
      ) : (
        <MdtReportPreview branding={previewBranding} structure={structure || {}} />
      )}
    </div>
  );
};

// --- Helpers ---

const composeAddress = (h: Hospital): string =>
  [h.addressline1, h.addressline2, h.city, h.state, h.country, h.postcode].filter(Boolean).join(", ");

const composeContact = (h: Hospital): string =>
  [h.officialPhone, h.officialEmail, h.contactPersonName].filter(Boolean).join(" · ");

const Card = ({
  icon,
  title,
  description,
  children,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
    <div className="px-6 pt-5 pb-4 border-b border-gray-100">
      <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
        {icon}
        {title}
      </h2>
      {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const Field = ({
  label,
  name,
  value,
  onChange,
  error,
  required,
  placeholder,
  textarea,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  textarea?: boolean;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={3}
        className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
          error ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"
        }`}
      />
    ) : (
      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
          error ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"
        }`}
      />
    )}
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

const TabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 text-sm font-medium transition-colors ${
      active
        ? "border-primary-600 text-primary-700"
        : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"
    }`}
  >
    {children}
  </button>
);

export default MDTTemplateEditor;